import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import {
  ArrowLeft,
  Plus,
  ClipboardList,
  Users,
  Flame,
  UtensilsCrossed,
  Calendar,
  Pencil,
  Trash2,
  ClipboardCheck,
} from "lucide-react";
import MealPlanTemplateLibrary from "./MealPlanTemplateLibrary";

const GOAL_BADGES = {
  bulking: "bg-orange-500/15 text-orange-400 border-orange-500/25",
  cutting: "bg-rose-500/15 text-rose-400 border-rose-500/25",
  maintenance: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  clean_eating: "bg-amber-500/15 text-amber-400 border-amber-500/25",
};

export default function NutritionPlanLibraryModal({
  open,
  onClose,
  plans = [],
  onCreatePlan,
  onSelectPlan,
  onEditPlan,
  onDeletePlan,
  onOpenScheduleDialog,
}) {
  const [activeTab, setActiveTab] = useState("my-plans");

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#030A17] flex flex-col overflow-y-auto text-white animate-in fade-in duration-200"
      data-testid="nutrition-plan-library-modal"
    >
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-[#030A17]/95 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition cursor-pointer"
            aria-label="Back to Meals"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-base font-bold text-white leading-tight">Nutrition Plans</h1>
            <p className="text-[10px] text-slate-400">Library & Community</p>
          </div>
        </div>

        {activeTab === "my-plans" && (
          <button
            type="button"
            onClick={onCreatePlan}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition cursor-pointer"
            data-testid="library-new-plan-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Plan</span>
          </button>
        )}
      </div>

      <div className="w-full max-w-3xl mx-auto px-4"><HealthSourceNote topic="planning" /></div>
      {/* Tabs Switcher: My Plans vs Community */}
      <div className="w-full max-w-3xl mx-auto px-4 pt-3">
        <div className="p-1 rounded-2xl bg-[#071326] border border-white/10 grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => setActiveTab("my-plans")}
            className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer ${
              activeTab === "my-plans"
                ? "bg-gradient-to-r from-sky-500 to-cyan-400 text-slate-950 shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5" />
            <span>My Plans ({plans.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("community")}
            className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer ${
              activeTab === "community"
                ? "bg-gradient-to-r from-sky-500 to-cyan-400 text-slate-950 shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Community</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 w-full max-w-3xl mx-auto px-4 py-4 pb-20">
        {activeTab === "my-plans" && (
          <div className="space-y-3">
            {plans.length === 0 ? (
              <div className="p-8 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-3">
                <UtensilsCrossed className="w-8 h-8 text-slate-500 mx-auto" />
                <h3 className="text-xs font-bold text-white">No Meal Plans Yet</h3>
                <p className="text-[11px] text-slate-400 max-w-xs mx-auto">
                  Create custom meal plans with targeted calories, macros, and scheduled meals.
                </p>
                <button
                  type="button"
                  onClick={onCreatePlan}
                  className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md transition cursor-pointer"
                >
                  Create Your First Plan
                </button>
              </div>
            ) : (
              plans.map((plan) => {
                const mealCount = Array.isArray(plan.meals) ? plan.meals.length : 0;
                const goalClass =
                  GOAL_BADGES[(plan.goal || "").toLowerCase()] ||
                  "bg-sky-500/15 text-sky-400 border-sky-500/25";

                return (
                  <div
                    key={plan.id}
                    className="p-4 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition shadow-sm space-y-3"
                    data-testid={`library-plan-${plan.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className="min-w-0 flex-1 cursor-pointer"
                        onClick={() => onSelectPlan(plan)}
                      >
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-sm font-bold text-white truncate">
                            {plan.name}
                          </h3>
                          {plan._assigned && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[9px] font-bold">
                              <ClipboardCheck className="w-2.5 h-2.5" /> Coach Assigned
                            </span>
                          )}
                          {plan.goal && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-[9px] font-bold border capitalize ${goalClass}`}
                            >
                              {plan.goal.replace("_", " ")}
                            </span>
                          )}
                        </div>

                        {plan.description && (
                          <p className="text-[11px] text-slate-400 line-clamp-1 mb-2">
                            {plan.description}
                          </p>
                        )}

                        <div className="flex items-center gap-3 text-[11px] text-slate-300">
                          {plan.daily_calories && (
                            <span className="flex items-center gap-1 font-semibold text-orange-400">
                              <Flame className="w-3.5 h-3.5" />
                              {plan.daily_calories} kcal
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-slate-400">
                            <UtensilsCrossed className="w-3.5 h-3.5 text-sky-400" />
                            {mealCount} meal{mealCount !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>

                      {/* Plan Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {onOpenScheduleDialog && (
                          <button
                            type="button"
                            onClick={() => onOpenScheduleDialog(plan)}
                            className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition cursor-pointer"
                            title="Assign to Day"
                          >
                            <Calendar className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!plan._assigned && onEditPlan && (
                          <button
                            type="button"
                            onClick={() => onEditPlan(plan)}
                            className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition cursor-pointer"
                            title="Edit Plan"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!plan._assigned && onDeletePlan && (
                          <button
                            type="button"
                            onClick={() => onDeletePlan(plan.id)}
                            className="p-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-white/10 active:scale-95 transition cursor-pointer"
                            title="Delete Plan"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === "community" && (
          <div className="space-y-4">
            <MealPlanTemplateLibrary />
          </div>
        )}
      </div>
    </div>
  );
}
